import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import { saveOrShareDataUrl, saveOrShareBlob } from './nativeFile';

interface ExportOptions {
  filename?: string;
  /** Background do export (PNG transparente se omitido). */
  backgroundColor?: string;
  /** Pixel ratio (qualidade). */
  pixelRatio?: number;
}

const PIXEL_TRANSPARENTE =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

/** Captura um elemento e devolve dataURL PNG. */
async function captureToPng(element: HTMLElement, opts: ExportOptions = {}): Promise<string> {
  return await toPng(element, {
    // Sem `cacheBust`: ele acrescenta um parâmetro na URL, o que invalida a assinatura dos links presigned
    // do storage (fotos de perfil e figuras enviadas) — a imagem vinha 403 e sumia do export. `no-store`
    // evita reaproveitar a resposta cacheada pelo <img> (sem cabeçalho de CORS).
    cacheBust: false,
    fetchRequestInit: { cache: 'no-store' },
    // Imagem que não puder ser lida vira um pixel transparente em vez de um ícone quebrado.
    imagePlaceholder: PIXEL_TRANSPARENTE,
    pixelRatio: opts.pixelRatio ?? 2,
    backgroundColor: opts.backgroundColor,
    filter: (node) => {
      // Ignora controles do ReactFlow (botões de zoom, minimap)
      const cls = (node as HTMLElement).classList;
      if (!cls) return true;
      if (cls.contains('react-flow__minimap')) return false;
      if (cls.contains('react-flow__controls')) return false;
      if (cls.contains('react-flow__panel')) return false;
      return true;
    },
  });
}

/** Exporta o elemento como PNG e dispara download (ou compartilhamento nativo no app). */
export async function exportElementAsPng(element: HTMLElement, opts: ExportOptions = {}) {
  const dataUrl = await captureToPng(element, {
    backgroundColor: opts.backgroundColor || '#ffffff',
    ...opts,
  });
  await saveOrShareDataUrl(dataUrl, opts.filename || 'fluxograma.png');
}

/** Exporta o elemento como PDF (uma página, ajustada ao conteúdo) e dispara download. */
export async function exportElementAsPdf(element: HTMLElement, opts: ExportOptions = {}) {
  const dataUrl = await captureToPng(element, {
    backgroundColor: opts.backgroundColor || '#ffffff',
    ...opts,
  });
  const img = new Image();
  img.src = dataUrl;
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = (e) => reject(e);
  });

  const orientation = img.width >= img.height ? 'landscape' : 'portrait';
  const pdf = new jsPDF({ orientation, unit: 'pt', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();

  // Fit
  const ratio = Math.min(pageW / img.width, pageH / img.height);
  const w = img.width * ratio;
  const h = img.height * ratio;
  const x = (pageW - w) / 2;
  const y = (pageH - h) / 2;
  pdf.addImage(dataUrl, 'PNG', x, y, w, h);
  const blob = pdf.output('blob');
  await saveOrShareBlob(blob, opts.filename || 'fluxograma.pdf');
}
